import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useOnClickOutside, useAppContext } from 'hooks';
import { setSessionStorage, getSessionStorage } from 'utils';
import { DEV_FEATURES, devFeaturesStorageKey } from './devFeatures';
import { getFeatures, featuresStorageKey, Features, FeatureKey } from '_config';
import { setError } from 'context';
import { ErrorType } from 'types';

const Container = styled.div`
  display: inline-block;
  height: 20px;
  position: fixed;
  right: 0;
  top: 0;
  overflow: visible;
  z-index: 1000;
`;

const Button = styled.button<{ close?: boolean; hide?: boolean }>`
  display: inline-block;
  width: 20px;
  height: 20px;
  appearance: none;
  border: 0;
  &:focus {
    outline: 0;
  }
  color: #ff9900;
  background-color: #ffffff;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="512" viewBox="-2 0 511 512" width="512" fill="%23FF9900"><path d="m500.628906 311.332031-27.960937-16.144531c-30.160157-17.414062-30.1875-60.949219 0-78.375l27.960937-16.144531c7.171875-4.144531 9.628906-13.316407 5.488282-20.488281l-60.242188-104.355469c-4.136719-7.167969-13.316406-9.632813-20.492188-5.492188l-27.960937 16.144531c-30.15625 17.410157-67.863281-4.324218-67.863281-39.1875v-32.289062c0-8.285156-6.714844-15-15-15h-120.488282c-8.28125 0-15 6.714844-15 15v32.289062c0 34.828126-37.671874 56.617188-67.863281 39.1875l-27.960937-16.144531c-7.167969-4.140625-16.347656-1.683593-20.492188 5.492188l-60.242187 104.355469c-4.140625 7.171874-1.683594 16.347656 5.488281 20.488281l27.960938 16.144531c30.160156 17.414062 30.1875 60.949219 0 78.375l-27.957032 16.144531c-7.175781 4.144531-9.632812 13.316407-5.492187 20.488281l60.242187 104.355469c4.136719 7.167969 13.316406 9.636719 20.492188 5.492188l27.960937-16.144531c30.152344-17.414063 67.863281 4.320312 67.863281 39.1875v32.289062c0 8.285156 6.714844 15 15 15h120.484376c8.285156 0 15-6.714844 15-15v-32.289062c0-34.820313 37.671874-56.621094 67.867187-39.1875l27.960937 16.144531c7.167969 4.140625 16.347657 1.683593 20.488282-5.492188l60.246094-104.355469c4.140624-7.171874 1.683593-16.34375-5.488282-20.488281zm-73.238281 96.855469-14.96875-8.644531c-50.160156-28.964844-112.863281 7.195312-112.863281 65.167969v17.289062h-90.488282v-17.289062c0-57.921876-62.664062-94.15625-112.863281-65.167969l-14.96875 8.644531-45.246093-78.375 14.96875-8.644531c50.15625-28.957031 50.207031-101.347657.003906-130.335938l-14.972656-8.644531 45.246093-78.375 14.96875 8.644531c50.160157 28.964844 112.863281-7.195312 112.863281-65.167969v-17.289062h90.488282v17.289062c0 57.921876 62.660156 94.15625 112.863281 65.167969l14.96875-8.644531 45.246094 78.375-14.96875 8.644531c-50.15625 28.957031-50.207031 101.347657-.003907 130.335938l14.972657 8.644531zm0 0" /><path d="m254.316406 151c-57.894531 0-104.992187 47.101562-104.992187 105s47.097656 105 104.992187 105c57.890625 0 104.988282-47.101562 104.988282-105s-47.097657-105-104.988282-105zm0 180c-41.351562 0-74.992187-33.644531-74.992187-75s33.640625-75 74.992187-75c41.347656 0 74.988282 33.644531 74.988282 75s-33.640626 75-74.988282 75zm0 0" /></svg>');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 16px 16px;

  ${(props) =>
    props.close
      ? `
  position: fixed;
  right: 0;
  top: 0;
  background: none;
  background-image: none;
  `
      : ''}
  opacity: ${(props) => (props.hide ? '0' : '1')}
`;

const Panel = styled.div`
  display: inline-block;
  position: fixed;
  right: 0;
  top: 0;
  background-color: rgba(255, 255, 255, 0.95);
  border: 1px solid #ff990070;
  padding: 20px;
  max-height: 100vh;
  box-sizing: border-box;
  overflow: auto;

  & > h3 {
    margin-top: 0;
  }
`;

const Toggle = styled.button`
  display: inline-block;
  width: 100%;
  height: 20px;
  appearance: none;
  border: 0;
  &:focus {
    outline: 0;
  }
`;

const ToggleContent = styled.div<{ show?: boolean }>`
  overflow: hidden;
  height: ${(p) => (p.show ? 'auto' : 0)};
`;

const EnvVar = styled.label<{ hasOverride?: boolean }>`
  position: relative;
  display: flex;
  font-size: 12px;
  align-items: center;
  margin: 6px 0px;
  & input {
    flex: 1 0 auto;
    margin-left: 6px;
    color: ${(p) => (p.hasOverride ? 'black' : 'grey')};
    &:read-only {
      border-color: transparent;
    }
  }
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const EnvVarResetButton = styled.button<{ show?: boolean }>`
  display: inline-block;
  appearance: none;
  border: 0;
  &:focus {
    outline: 0;
  }
  position: absolute;
  top: 1px;
  right: 1px;
  height: 19px;
  width: auto;
  opacity: ${(p) => (p.show ? 1 : 0)};
`;

const Feature = styled.div<{ as?: string }>`
  white-space: nowrap;
  margin-bottom: 10px;
  font-size: 14px;
  & > label {
    padding-left: 10px;
    font-size: 14px;
  }
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const TEALIUM_DEBUG_MODE = 'TEALIUM_DEBUG_MODE';
const TEALIUM_DEBUG_MODE_STORAGE_KEY = 'tealiumDebugMode';
const PROCESS_ENV_VARS = Object.keys(process.env).reduce((acc, key) => {
  return key.includes('REACT_APP') ? { ...acc, [key]: process.env[key] } : acc;
}, {});
const EDITABLE_ENV_VARS = ['REACT_APP_SESSION_EXPIRATION_IN_SEC'];

export const DevFeatureToggle = () => {
  const [active, setActive] = useState(false);
  const [tealiumDebugMode, setTealiumDebugMode] = useState(false);
  const { dispatch } = useAppContext();
  const [verifyNumber, setVerifyNumber] = useState(
    DEV_FEATURES.OTP_MOBILE_NUMBER || process.env.REACT_APP_DEFAULT_OTP_MOBILE_NUMBER || '',
  );
  const [showEnv, setShowEnv] = useState(false);
  const [showDevFeatures, setShowDevFeatures] = useState(true);
  const [showFeatures, setShowFeatures] = useState(true);
  const onChangeVerifyNumber = useCallback((event) => setVerifyNumber(event.target.value), []);
  const [envOverrides, setEnvOverrides] = useState(getSessionStorage(devFeaturesStorageKey, 'ENV_OVERRIDES'));
  const FEATURES = getFeatures();
  const allFeatures = { ...FEATURES, ...DEV_FEATURES };

  const ref = useOnClickOutside(() => {
    setActive(false);
  });

  const handleOpenClick = () => {
    setActive(true);
  };

  const handleCloseClick = (): void => {
    setActive(false);
  };

  const handleToggleEnvClick = (): void => {
    setShowEnv(!showEnv);
  };

  const handleToggleDevFeaturesClick = (): void => {
    setShowDevFeatures(!showDevFeatures);
  };

  const handleToggleFeaturesClick = (): void => {
    setShowFeatures(!showFeatures);
  };

  const updateFeature = useCallback(
    (featureName, value) => {
      if (value !== undefined) {
        if (FEATURES.hasOwnProperty(featureName)) {
          setSessionStorage(featuresStorageKey, { ...FEATURES, [featureName]: value });
        } else if (DEV_FEATURES[featureName] !== undefined) {
          DEV_FEATURES[featureName] = value;
          setSessionStorage(devFeaturesStorageKey, DEV_FEATURES);
        }
      }
    },
    [FEATURES],
  );

  const handleEnvValueChange = useCallback(
    (evt: React.SyntheticEvent<HTMLInputElement>) => {
      const envName = evt.currentTarget.name;
      const envValue = evt.currentTarget.value;
      setEnvOverrides({ ...envOverrides, [envName]: envValue });
    },
    [envOverrides],
  );

  const handleEnvVarReset = (evt: React.SyntheticEvent<HTMLButtonElement>): void => {
    const key = evt.currentTarget.getAttribute('data-id') as string;
    setEnvOverrides({ ...envOverrides, [key]: null });
  };

  const handleCheckboxOnChange = (evt: React.SyntheticEvent<HTMLInputElement>): void => {
    if (evt.currentTarget.value === TEALIUM_DEBUG_MODE) {
      if (evt.currentTarget.checked) {
        localStorage.setItem(TEALIUM_DEBUG_MODE_STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(TEALIUM_DEBUG_MODE_STORAGE_KEY);
      }
      return window.location.reload();
    }

    updateFeature(evt.currentTarget.value, evt.currentTarget.checked);

    if (
      ['HIDE_DEV_WIDGETS', 'SHOW_FORM_DEBUG', 'PREVENT_UNLOAD_MESSAGE', ...Object.keys(FEATURES)].includes(
        evt.currentTarget.value,
      )
    ) {
      window.location.reload();
    }
  };

  const clearApplicationErrors = (): void => {
    dispatch(setError(null));
  };

  const mockSubmissionNetworkError = (): void => {
    dispatch(
      setError({
        type: ErrorType.APOLLO_NETWORK_ERROR,
        error: {
          message: 'Server error',
          code: '500',
        },
      }),
    );
  };

  const activateAllFeatures = (): void => {
    // FIXME: this mutates object
    Object.keys(FEATURES as Features).forEach((key) => {
      FEATURES[key as FeatureKey] = true;
    });
    setSessionStorage(featuresStorageKey, FEATURES);
    window.location.reload();
  };

  const disableAllFeatures = (): void => {
    // FIXME: this mutates object
    Object.keys(FEATURES as Features).forEach((key) => {
      FEATURES[key as FeatureKey] = false;
    });
    setSessionStorage(featuresStorageKey, FEATURES);
    window.location.reload();
  };

  useEffect((): void => {
    if (localStorage.getItem(TEALIUM_DEBUG_MODE_STORAGE_KEY) === 'true') {
      setTealiumDebugMode(true);
    }
    setSessionStorage(featuresStorageKey, FEATURES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateFeature('OTP_MOBILE_NUMBER', verifyNumber);
  }, [updateFeature, verifyNumber]);

  useEffect(() => {
    updateFeature('ENV_OVERRIDES', envOverrides);
  }, [envOverrides, updateFeature]);

  if (process.env.REACT_APP_ENV === 'prod') {
    return null;
  }

  return (
    <Container ref={ref} style={{ position: 'fixed', zIndex: 1000 }}>
      <Button onClick={handleOpenClick} hide={allFeatures.HIDE_DEV_WIDGETS} data-testid="dev-feature-toggle-open" />
      {active && (
        <Panel>
          <Feature>
            <Toggle onClick={handleToggleDevFeaturesClick}>Dev</Toggle>
            <ToggleContent show={showDevFeatures}>
              <Feature as="button" onClick={clearApplicationErrors}>
                Clear application errors
              </Feature>
              <Feature as="button" onClick={mockSubmissionNetworkError}>
                Mock submission network error
              </Feature>
              <Feature>
                OTP Override Mobile:{' '}
                <input
                  placeholder="+61412345678"
                  value={verifyNumber}
                  onChange={onChangeVerifyNumber}
                  data-testid="dev-override-otp-mobile-value"
                />
              </Feature>
              {Object.keys(DEV_FEATURES).map((feature) => {
                return typeof allFeatures[feature] === 'boolean' ? (
                  <Feature key={feature}>
                    <input
                      key={feature}
                      type="checkbox"
                      id={feature}
                      name={feature}
                      value={feature}
                      defaultChecked={allFeatures[feature]}
                      onChange={handleCheckboxOnChange}
                    />
                    <label id="" htmlFor={feature}>
                      {feature}
                    </label>
                  </Feature>
                ) : null;
              })}
              <Feature>
                <input
                  key={TEALIUM_DEBUG_MODE}
                  type="checkbox"
                  id={TEALIUM_DEBUG_MODE}
                  name={TEALIUM_DEBUG_MODE}
                  value={TEALIUM_DEBUG_MODE}
                  defaultChecked={tealiumDebugMode}
                  onChange={handleCheckboxOnChange}
                />
                <label id="" htmlFor={TEALIUM_DEBUG_MODE}>
                  {TEALIUM_DEBUG_MODE}
                </label>
              </Feature>
            </ToggleContent>
          </Feature>
          <Feature>
            <Toggle onClick={handleToggleFeaturesClick}>Features</Toggle>
            <ToggleContent show={showFeatures}>
              <Feature as="button" onClick={activateAllFeatures}>
                Activate all features
              </Feature>
              <Feature as="button" onClick={disableAllFeatures}>
                Disable all features
              </Feature>
              {Object.keys(FEATURES).map((feature) => {
                return typeof allFeatures[feature] === 'boolean' ? (
                  <Feature key={feature}>
                    <input
                      key={feature}
                      type="checkbox"
                      id={feature}
                      name={feature}
                      value={feature}
                      defaultChecked={allFeatures[feature]}
                      onChange={handleCheckboxOnChange}
                    />
                    <label id="" htmlFor={feature}>
                      {feature}
                    </label>
                  </Feature>
                ) : null;
              })}
            </ToggleContent>
          </Feature>
          {PROCESS_ENV_VARS && (
            <Feature>
              <Toggle onClick={handleToggleEnvClick} data-testid="dev-env-override-btn">
                Override ENV
              </Toggle>
              <ToggleContent show={showEnv}>
                {Object.keys(PROCESS_ENV_VARS).map((key) => {
                  const isReadOnly = !EDITABLE_ENV_VARS.includes(key);
                  const overrideValue = envOverrides?.[key];
                  const hasOverride = typeof overrideValue === 'string';
                  const origValue = process.env[key];
                  return (
                    <EnvVar key={key} hasOverride={hasOverride}>
                      {`${key} `}
                      <input
                        name={key}
                        value={hasOverride ? overrideValue : origValue}
                        onChange={handleEnvValueChange}
                        readOnly={isReadOnly}
                      />
                      <EnvVarResetButton data-id={key} onClick={handleEnvVarReset} show={hasOverride}>
                        X
                      </EnvVarResetButton>
                    </EnvVar>
                  );
                })}
              </ToggleContent>
            </Feature>
          )}

          <Button onClick={handleCloseClick} close data-testid="dev-feature-toggle-close">
            X
          </Button>
        </Panel>
      )}
    </Container>
  );
};
